/**
 * PildoraAlumneDetailModal.tsx
 * Shows the submission history of ONE student for ONE pildora (caza):
 * submitted code, test output, statement/steps and the unit tests configured
 * per step. Reuses the same DAOs as CazaAdminModal but scoped to a single student.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Collapse, Tag, Space, Typography, Button, Tooltip, message, Drawer, Statistic, Row, Col } from 'antd';
import { CheckCircleOutlined, DeleteOutlined, ExperimentOutlined, FileTextOutlined, ClockCircleOutlined, RedoOutlined } from '@ant-design/icons';
import Editor from '@monaco-editor/react';
import ReactMarkdown from 'react-markdown';
import { GetCazaHistoryAdmin } from '../../../../dao/jutge/GetCazaHistoryAdmin';
import { GetJutgeCazaTests } from '../../../../dao/jutge/GetJutgeCazaTests';
import { DeleteCazaAttempt } from '../../../../dao/jutge/DeleteCazaAttempt';

const { Text } = Typography;
const { Panel } = Collapse;

interface Props {
    visible: boolean;
    onClose: () => void;
    idCaza: string;
    idUsuari: string;
    pildoraTitle: string;
    studentName: string;
    totalSteps?: number;
    canDelete?: boolean;
    instrucciones?: string;          // raw steps/statement of the pildora (JSON array or text)
    tiempoEmpleadoSegundos?: number; // total time spent by this student
    idConjunt?: number | null;       // conjunt context for per-aula independent progress
}

interface StepInfo {
    paso: number;
    titulo: string;
    instrucciones: string;
}

// Parse the pildora statement into steps (same formats supported by NouCazaModal)
const parseSteps = (raw?: string): StepInfo[] => {
    if (!raw || !raw.trim()) return [];
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) {
            return parsed.map((p: any, idx: number) => ({
                paso: p.paso || idx + 1,
                titulo: p.titulo || `Paso ${idx + 1}`,
                instrucciones: p.instrucciones || ''
            }));
        }
    } catch (e) { /* not json */ }
    const split = raw.split(/(?=###\s*Paso|\bPASO\s+\d+:?)/i).filter(s => s.trim().length > 0);
    if (split.length > 1) {
        return split.map((s, idx) => {
            const lines = s.trim().split('\n');
            return { paso: idx + 1, titulo: lines[0].replace(/^[#\s*]+/, '').trim() || `Paso ${idx + 1}`, instrucciones: lines.slice(1).join('\n').trim() };
        });
    }
    return [{ paso: 1, titulo: 'Enunciat', instrucciones: raw.trim() }];
};

const formatSec = (sec?: number): string => {
    if (sec === undefined || sec === null) return '—';
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}m ${s < 10 ? '0' : ''}${s}s`;
};

// Decode a configured test (same logic used in CazaAdminModal)
const decodeTest = (t: any): { input: string; expected: string; typeLabel: string; tagColor: string } => {
    const tipus = (t.tipus || 'es').toLowerCase();
    let input = '';
    let expected = '';
    let tagColor = 'blue';
    let typeLabel = 'E/S';

    try {
        if (tipus === 'content') {
            tagColor = 'green';
            typeLabel = 'CONTENIDO';
            let jsonStr = '';
            if (t.fitxer?.startsWith('CONTENT:DATA:')) jsonStr = window.atob(t.fitxer.substring('CONTENT:DATA:'.length).trim());
            else if (t.fitxer?.startsWith('{')) jsonStr = t.fitxer;
            if (jsonStr) {
                const parsed = JSON.parse(jsonStr);
                expected = (parsed.mustContain !== false ? 'Debe contener: ' : 'No debe contener: ') + `"${parsed.contentText || ''}"`;
            }
        } else if (tipus === 'method' || tipus === 'methodtest') {
            tagColor = 'purple';
            typeLabel = 'MÉTODO';
            let jsonStr = '';
            if (t.fitxer?.startsWith('METHOD:DATA:')) jsonStr = window.atob(t.fitxer.substring('METHOD:DATA:'.length).trim());
            else if (t.fitxer?.startsWith('{')) jsonStr = t.fitxer;
            if (jsonStr) {
                const parsed = JSON.parse(jsonStr);
                expected = `Método: ${parsed.returnType || 'void'} ${parsed.methodName || ''}(...)`;
                if (parsed.entrada) input = parsed.entrada;
                if (parsed.sortida) expected += ` -> Retorna/Imprime: ${parsed.sortida}`;
            }
        } else {
            // E/S
            if (t.fitxer) {
                const parts = t.fitxer.split(':', 3);
                if (parts.length >= 2) { try { input = window.atob(parts[1].trim()); } catch { input = parts[1]; } }
                if (parts.length >= 3) { try { expected = window.atob(parts[2].trim()); } catch { expected = parts[2]; } }
            }
        }
    } catch (e) {
        expected = t.fitxer || '';
    }
    return { input, expected, typeLabel, tagColor };
};

const PildoraAlumneDetailModal: React.FC<Props> = ({
    visible, onClose, idCaza, idUsuari, pildoraTitle, studentName, totalSteps = 1, canDelete = false,
    instrucciones, tiempoEmpleadoSegundos, idConjunt
}) => {
    const [history, setHistory] = useState<any[]>([]);
    const [cazaTests, setCazaTests] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [summaryOpen, setSummaryOpen] = useState(false);

    const steps = useMemo(() => parseSteps(instrucciones), [instrucciones]);

    // Attempts per step and total, derived from the submission history
    const attemptStats = useMemo(() => {
        const perStep: Record<number, { total: number; passed: boolean }> = {};
        let total = 0;
        history.forEach(h => {
            const step = h.ordre || 1;
            if (!perStep[step]) perStep[step] = { total: 0, passed: false };
            perStep[step].total += 1;
            if (h.estat === 1) perStep[step].passed = true;
            total += 1;
        });
        return { perStep, total };
    }, [history]);

    // Number of steps: prefer the configured tests count, fall back to parsed steps / prop
    const resolvedTotalSteps = useMemo(() => {
        const fromTests = new Set(cazaTests.map(t => t.ordre || 1)).size;
        return Math.max(fromTests, steps.length, totalSteps, 1);
    }, [cazaTests, steps.length, totalSteps]);

    useEffect(() => {
        if (visible && idCaza && idUsuari) {
            fetchAll();
        } else if (!visible) {
            setHistory([]);
            setCazaTests([]);
        }
        // eslint-disable-next-line
    }, [visible, idCaza, idUsuari]);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [hist, tests] = await Promise.all([
                GetCazaHistoryAdmin(idCaza, idUsuari, idConjunt),
                GetJutgeCazaTests(idCaza)
            ]);
            const sorted = Array.isArray(hist)
                ? [...hist].sort((a, b) => {
                    const timeDiff = new Date(b.moment || 0).getTime() - new Date(a.moment || 0).getTime();
                    if (timeDiff !== 0) return timeDiff;
                    return (Number(b.id) || 0) - (Number(a.id) || 0);
                })
                : [];
            setHistory(sorted);
            setCazaTests(Array.isArray(tests) ? tests : []);
        } catch (e) {
            message.error('Error al cargar el detalle del alumno');
        }
        setLoading(false);
    };

    const handleDelete = async (attemptId: string) => {
        try {
            await DeleteCazaAttempt(attemptId);
            message.success('Intento eliminado y estadísticas recalculadas');
            fetchAll();
        } catch (e) {
            message.error('Error al eliminar intento');
        }
    };

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingRight: 32 }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {pildoraTitle} — {studentName}
                    </span>
                    <Tooltip title="Veure enunciat i tests per fase">
                        <Button
                            type="primary"
                            icon={<FileTextOutlined />}
                            onClick={() => setSummaryOpen(true)}
                            style={{ flexShrink: 0 }}
                        >
                            Enunciat i tests
                        </Button>
                    </Tooltip>
                </div>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            width={900}
            bodyStyle={{ maxHeight: '72vh', overflowY: 'auto' }}
        >
            {/* Métricas globales siempre visibles */}
            <Row gutter={12} style={{ marginBottom: 16, background: '#fafafa', borderRadius: 8, padding: '8px 4px' }}>
                <Col span={8}>
                    <Statistic title="Intents totals" value={attemptStats.total} prefix={<RedoOutlined />} valueStyle={{ fontSize: 20 }} />
                </Col>
                <Col span={8}>
                    <Statistic title="Passos" value={resolvedTotalSteps} prefix={<CheckCircleOutlined />} valueStyle={{ fontSize: 20 }} />
                </Col>
                <Col span={8}>
                    <Statistic title="Temps" value={formatSec(tiempoEmpleadoSegundos)} prefix={<ClockCircleOutlined />} valueStyle={{ fontSize: 20 }} />
                </Col>
            </Row>

            {loading ? (
                <div style={{ textAlign: 'center', padding: 50 }}>Cargando historial de envíos...</div>
            ) : history.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#888', padding: 40 }}>
                    Este alumno aún no ha realizado ningún envío en esta píldora.
                </div>
            ) : (
                <Collapse accordion defaultActiveKey={[history[0]?.id]}>
                    {history.map((attempt) => {
                        const isPassed = attempt.estat === 1;
                        const isPending = attempt.estat === -1;
                        const stepNum = attempt.ordre || 1;
                        const stepTests = cazaTests.filter(t => (t.ordre || 1) === stepNum);

                        return (
                            <Panel
                                key={attempt.id}
                                header={
                                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                        <Space>
                                            <Tag color="purple" style={{ fontWeight: 'bold' }}>Paso {stepNum}{totalSteps > 1 ? ` / ${totalSteps}` : ''}</Tag>
                                            <Tag color={isPassed ? 'green' : isPending ? 'orange' : 'red'}>
                                                {isPassed ? 'CORRECTO' : isPending ? 'EVALUANDO' : 'INCORRECTO'}
                                            </Tag>
                                            <span style={{ fontSize: 12, color: '#666' }}>{attempt.moment}</span>
                                        </Space>
                                        {canDelete && (
                                            <Tooltip title="Eliminar intento">
                                                <Button
                                                    type="text"
                                                    danger
                                                    size="small"
                                                    icon={<DeleteOutlined />}
                                                    onClick={(e) => { e.stopPropagation(); handleDelete(attempt.id); }}
                                                />
                                            </Tooltip>
                                        )}
                                    </div>
                                }
                            >
                                <div style={{ marginBottom: 12 }}>
                                    <strong>Intentos registrados en ese momento:</strong> {attempt.intent}
                                </div>

                                {/* Unit tests configured for this step (statement + expected) */}
                                {stepTests.length > 0 && (
                                    <div style={{ marginBottom: 16 }}>
                                        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, color: '#1d39c4', display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <ExperimentOutlined /> Tests configurados para el Paso {stepNum} ({stepTests.length}):
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: stepTests.length > 1 ? '1fr 1fr' : '1fr', gap: 8 }}>
                                            {stepTests.map((t, idx) => {
                                                const { input, expected, typeLabel, tagColor } = decodeTest(t);
                                                return (
                                                    <div key={t.id || idx} style={{ background: '#f0f5ff', border: '1px solid #adc6ff', borderRadius: 6, padding: 8, fontSize: 12 }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                                            <span style={{ fontWeight: 600, color: '#1d39c4' }}>{t.name || `Test #${idx + 1}`}</span>
                                                            <Tag color={tagColor} style={{ fontSize: 10, lineHeight: '18px', padding: '0 4px', margin: 0 }}>{typeLabel}</Tag>
                                                        </div>
                                                        {input && input.trim().length > 0 && (
                                                            <div style={{ marginBottom: 4 }}>
                                                                <span style={{ color: '#595959', fontWeight: 500 }}>Entrada: </span>
                                                                <pre style={{ margin: 0, padding: '2px 4px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: 3, maxHeight: 60, overflow: 'auto' }}>{input}</pre>
                                                            </div>
                                                        )}
                                                        <div>
                                                            <span style={{ color: '#595959', fontWeight: 500 }}>
                                                                {(t.tipus || '').toLowerCase() === 'content' ? 'Condición de código: ' : 'Salida esperada: '}
                                                            </span>
                                                            <pre style={{ margin: 0, padding: '2px 4px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: 3, maxHeight: 60, overflow: 'auto' }}>{expected || '(vacía)'}</pre>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Validation output */}
                                <div style={{ marginBottom: 16 }}>
                                    <strong>Resultado / Salida de la validación:</strong>
                                    <pre style={{
                                        background: isPassed ? '#f6ffed' : '#fff1f0',
                                        border: isPassed ? '1px solid #b7eb8f' : '1px solid #ffa39e',
                                        padding: 10, borderRadius: 4, maxHeight: 160, overflow: 'auto',
                                        color: isPassed ? '#389e0d' : '#cf1322', whiteSpace: 'pre-wrap', marginTop: 4
                                    }}>
                                        {attempt.errors && attempt.errors.trim().length > 0 && (!attempt.errors.trim().startsWith('OK') || isPassed)
                                            ? attempt.errors
                                            : (isPassed
                                                ? 'Todos los tests superados correctamente'
                                                : 'El código no produjo el resultado esperado para las pruebas de este paso.')}
                                    </pre>
                                </div>

                                {/* Submitted code */}
                                <div>
                                    <strong>Código enviado por el alumno:</strong>
                                    <div style={{ height: 320, border: '1px solid #d9d9d9', borderRadius: 4, overflow: 'hidden', marginTop: 6 }}>
                                        <Editor
                                            height="100%"
                                            defaultLanguage="java"
                                            value={attempt.actual || '// No code'}
                                            options={{ readOnly: true, minimap: { enabled: false } }}
                                        />
                                    </div>
                                </div>
                            </Panel>
                        );
                    })}
                </Collapse>
            )}

            {/* Summary Drawer: statement + tests per step, attempts per step/total, time */}
            <Drawer
                title={`Enunciat i tests: ${pildoraTitle}`}
                placement="right"
                width={520}
                open={summaryOpen}
                onClose={() => setSummaryOpen(false)}
            >
                {/* Per-step breakdown */}
                {Array.from({ length: resolvedTotalSteps }, (_, i) => i + 1).map(stepNum => {
                    const step = steps.find(s => s.paso === stepNum);
                    const stepTests = cazaTests.filter(t => (t.ordre || 1) === stepNum);
                    const stat = attemptStats.perStep[stepNum];
                    const attempts = stat?.total || 0;
                    const passed = stat?.passed || false;
                    return (
                        <div key={stepNum} style={{ marginBottom: 20, paddingBottom: 12, borderBottom: '1px dashed #eee' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, flexWrap: 'wrap', gap: 6 }}>
                                <Space>
                                    <Tag color="purple" style={{ fontWeight: 'bold', margin: 0 }}>Paso {stepNum}</Tag>
                                    <Text strong>{step?.titulo || `Paso ${stepNum}`}</Text>
                                </Space>
                                <Space>
                                    <Tag color={attempts > 0 ? 'blue' : 'default'} style={{ margin: 0 }}>
                                        <RedoOutlined /> {attempts} intents
                                    </Tag>
                                    <Tag color={passed ? 'green' : 'default'} style={{ margin: 0 }}>
                                        {passed ? 'Superat' : 'Pendent'}
                                    </Tag>
                                </Space>
                            </div>

                            {/* Statement */}
                            {step?.instrucciones && (
                                <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 6, padding: '6px 10px', fontSize: 13, marginBottom: 8 }}>
                                    <div style={{ fontWeight: 600, fontSize: 12, color: '#8c8c8c', marginBottom: 2 }}>Enunciat</div>
                                    <ReactMarkdown>{step.instrucciones}</ReactMarkdown>
                                </div>
                            )}

                            {/* Tests of this step */}
                            {stepTests.length > 0 ? (
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: 12, color: '#1d39c4', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <ExperimentOutlined /> Tests del pas ({stepTests.length})
                                    </div>
                                    {stepTests.map((t, idx) => {
                                        const { input, expected, typeLabel, tagColor } = decodeTest(t);
                                        return (
                                            <div key={t.id || idx} style={{ background: '#f0f5ff', border: '1px solid #adc6ff', borderRadius: 6, padding: 8, fontSize: 12, marginBottom: 6 }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                                    <span style={{ fontWeight: 600, color: '#1d39c4' }}>{t.name || `Test #${idx + 1}`}</span>
                                                    <Tag color={tagColor} style={{ fontSize: 10, lineHeight: '18px', padding: '0 4px', margin: 0 }}>{typeLabel}</Tag>
                                                </div>
                                                {input && input.trim().length > 0 && (
                                                    <div style={{ marginBottom: 4 }}>
                                                        <span style={{ color: '#595959', fontWeight: 500 }}>Entrada: </span>
                                                        <pre style={{ margin: 0, padding: '2px 4px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: 3, maxHeight: 80, overflow: 'auto' }}>{input}</pre>
                                                    </div>
                                                )}
                                                <div>
                                                    <span style={{ color: '#595959', fontWeight: 500 }}>
                                                        {(t.tipus || '').toLowerCase() === 'content' ? 'Condició de codi: ' : 'Sortida esperada: '}
                                                    </span>
                                                    <pre style={{ margin: 0, padding: '2px 4px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: 3, maxHeight: 80, overflow: 'auto' }}>{expected || '(buida)'}</pre>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <Text type="secondary" style={{ fontSize: 12 }}>Sense tests configurats en aquest pas.</Text>
                            )}
                        </div>
                    );
                })}
            </Drawer>
        </Modal>
    );
};

export default PildoraAlumneDetailModal;
